import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { Incident, NearbyStation, PoliceStation } from '../lib/types';
import { clock } from '../lib/format';
import { Banner, ConfirmButton } from './ui';

/**
 * Handing an incident to a police station.
 *
 * This is a referral, not a tasking. No one at the station signs in and nothing
 * waits on them to accept — the point is the record of where an incident went
 * and when, which is what the incident timeline and the audit log capture.
 *
 * Stations are offered nearest-first when the incident has coordinates, but the
 * full list stays one click away: jurisdiction follows boundaries, not radius,
 * and the dispatcher knows which side of one an address falls on.
 */
export function StationDispatch({
  incident,
  canDispatch,
  onChanged,
}: {
  incident: Incident;
  canDispatch: boolean;
  onChanged: () => void;
}) {
  const [stations, setStations] = useState<PoliceStation[]>([]);
  const [nearby, setNearby] = useState<NearbyStation[]>([]);
  const [choice, setChoice] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    void api.get<{ stations: PoliceStation[] }>('/stations')
      .then((r) => setStations(r.stations))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (incident.latitude == null || incident.longitude == null) {
      setNearby([]);
      return;
    }
    void api
      .get<{ stations: NearbyStation[] }>(
        `/stations/nearest?latitude=${incident.latitude}&longitude=${incident.longitude}&limit=5`,
      )
      .then((r) => setNearby(r.stations))
      .catch(() => undefined);
  }, [incident.latitude, incident.longitude]);

  const approximate = useMemo(
    () => nearby.length > 0 && nearby.some((s) => !s.location_verified),
    [nearby],
  );

  const send = async (stationId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/stations/dispatch/${incident.id}`, {
        stationId,
        reference: reference.trim() || null,
      });
      setReference('');
      setChoice('');
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.del(`/stations/dispatch/${incident.id}`);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Already sent: show where, and offer a re-route rather than a second copy.
  if (incident.station_id && !showAll) {
    return (
      <div className="stack tight">
        {error && <Banner kind="error">{error}</Banner>}
        <div className="row between">
          <div>
            <div className="row tight">
              <span className="badge accent">{incident.station_code}</span>
              <strong>{incident.station_name} police station</strong>
            </div>
            <div className="faint">
              Sent {clock(incident.station_dispatched_at)}
              {incident.station_reference && ` · ref ${incident.station_reference}`}
              {incident.station_phone && ` · ${incident.station_phone}`}
            </div>
          </div>
          {canDispatch && (
            <div className="row tight">
              <button className="btn sm" disabled={busy} onClick={() => setShowAll(true)}>
                Re-route
              </button>
              <ConfirmButton
                label="Withdraw"
                confirmLabel="Withdraw referral?"
                disabled={busy}
                onConfirm={() => void withdraw()}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!canDispatch) {
    // A call taker sees this. Saying only "not yet sent" reads like a broken
    // control; saying who sends it answers the question they are about to ask.
    return (
      <div className="stack tight">
        <p className="muted" style={{ margin: 0 }}>Not yet sent to a police station.</p>
        <p className="faint" style={{ margin: 0 }}>
          A dispatcher sends it from the Dispatch console. This incident is already in their queue.
        </p>
      </div>
    );
  }

  return (
    <div className="stack tight">
      {error && <Banner kind="error">{error}</Banner>}

      {nearby.length > 0 && (
        <>
          <h4 style={{ margin: 0 }}>Nearest to the incident</h4>
          <div className="row tight">
            {nearby.map((station) => (
              <button
                key={station.id}
                className="chip"
                disabled={busy}
                title={`${station.zone ?? ''} zone${station.phone ? ` · ${station.phone}` : ''}`}
                onClick={() => void send(station.id)}
              >
                {station.name} · {station.distance_km.toFixed(1)} km
              </button>
            ))}
          </div>
          {approximate && (
            <span className="faint">
              Distances are indicative — some station coordinates are approximate and unconfirmed.
              Jurisdiction follows boundaries, not distance.
            </span>
          )}
        </>
      )}

      <div className="row">
        <select value={choice} onChange={(e) => setChoice(e.target.value)} style={{ flex: 1 }}>
          <option value="">
            {incident.station_id ? 'Re-route to another station…' : 'Choose any station…'}
          </option>
          {stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.name}{station.zone ? ` (${station.zone})` : ''}
              {station.open_incidents > 0 ? ` — ${station.open_incidents} open` : ''}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Their reference (optional)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          style={{ width: 190 }}
        />
        <button className="btn primary" disabled={busy || !choice} onClick={() => void send(choice)}>
          Send
        </button>
        {showAll && (
          <button className="btn ghost" onClick={() => setShowAll(false)}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
